import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Wallet,
  TrendingUp,
  FileText,
  AlertCircle,
  Clock,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  Eye,
  PlusCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  fetchEmployeePFAccount,
  fetchEmployeePFLedger,
  fetchEmployeePFAdvances,
  fetchEmployeePFRecovery,
  approvePFAdvanceRequest,
  rejectPFAdvanceRequest,
  confirmPFAdvancePayment,
  downloadAdvanceDocument,
  submitEmployeePFAdvanceRequest
} from '../../services/pfAccountApi';
import {
  fetchEmployeeVpfContext,
  approveVpfRequest,
  rejectVpfRequest,
  closeVpfRequest,
  fetchEmployeeVpfHistory,
  createEmployeeVpfRequest
} from '../../services/vpfApi';
import { formatDate } from '../../utils/dateUtils';


const Employee360PFView = ({ employeeId, renderVpfTab }) => {
  const { hasPermission } = useAuth();
  const [subTab, setSubTab] = useState('overview');

  // Data state
  const [account, setAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [advances, setAdvances] = useState([]);
  const [recoveryInfo, setRecoveryInfo] = useState(null);
  const [vpfContext, setVpfContext] = useState(null);
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState('');
  const setError = (msg) => {
    setErrorState(msg || '');
    if (msg) showError(msg);
  };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  // Approve / Reject Dialog state
  const [approveModal, setApproveModal] = useState(null); // Request object
  const [approvedAmount, setApprovedAmount] = useState('');
  const [approveRemarks, setApproveRemarks] = useState('');
  const [transactionCompleted, setTransactionCompleted] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [approvePaymentDoc, setApprovePaymentDoc] = useState(null);

  const [rejectModal, setRejectModal] = useState(null); // Request object
  const [rejectionReason, setRejectionReason] = useState('');
  const [confirmPaymentModal, setConfirmPaymentModal] = useState(null);
  const [confirmPaymentMode, setConfirmPaymentMode] = useState('');
  const [confirmPaymentDoc, setConfirmPaymentDoc] = useState(null);

  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  // Create PF Request Dialog state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [requestType, setRequestType] = useState('REFUNDABLE');
  const [requestedAmount, setRequestedAmount] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');

  // VPF Admin States
  const [vpfHistory, setVpfHistory] = useState([]);
  const [isVpfModalOpen, setIsVpfModalOpen] = useState(false);
  const [vpfContributionType, setVpfContributionType] = useState('FIXED_AMOUNT');
  const [vpfFixedAmount, setVpfFixedAmount] = useState('');
  const [vpfPercentage, setVpfPercentage] = useState('');
  const [vpfEffectiveFrom, setVpfEffectiveFrom] = useState('');
  const [vpfEffectiveTo, setVpfEffectiveTo] = useState('');
  const [vpfSubmitting, setVpfSubmitting] = useState(false);
  const [vpfError, setVpfError] = useState('');
  const [vpfRejectionReason, setVpfRejectionReason] = useState('');
  const [rejectVpfModal, setRejectVpfModal] = useState(null); // Request object



  const loadEmployeePFData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [acc, adv, rec, leg, vpf, vpfHist] = await Promise.all([
        fetchEmployeePFAccount(employeeId),
        fetchEmployeePFAdvances(employeeId),
        fetchEmployeePFRecovery(employeeId),
        fetchEmployeePFLedger(employeeId, 1, 20),
        fetchEmployeeVpfContext(employeeId).catch(() => null),
        fetchEmployeeVpfHistory(employeeId).catch(() => [])
      ]);
      setAccount(acc);
      setAdvances(adv);
      setRecoveryInfo(rec);
      setLedgerData(leg);
      setVpfContext(vpf);
      setVpfHistory(vpfHist || []);
    } catch (err) {
      setError(err.message || 'Failed to load employee PF data.');
    } finally {
      setLoading(false);
    }

  }, [employeeId]);

  useEffect(() => {
    if (employeeId) {
      loadEmployeePFData();
    }
  }, [employeeId, loadEmployeePFData]);

  // Component-level permission check
  if (!hasPermission('emp:pfsubsys:view') && !hasPermission('*')) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center space-x-2">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <span>You do not have permission to view PF details.</span>
      </div>
    );
  }

  const handlePageChange = async (newPage) => {
    try {
      const leg = await fetchEmployeePFLedger(employeeId, newPage, ledgerData.page_size);
      setLedgerData(leg);
    } catch (err) {
      setError(err.message || 'Failed to load ledger page.');
    }
  };

  // Open Approval Modal
  const handleOpenApprove = (req) => {
    setApproveModal(req);
    setApprovedAmount(req.requested_amount ? String(req.requested_amount) : '');
    setApproveRemarks('');
    setTransactionCompleted('');
    setPaymentMode('');
    setApprovePaymentDoc(null);
    setActionError('');
  };

  // Submit Approval
  const handleSubmitApprove = async (e) => {
    e.preventDefault();
    setActionSubmitting(true);
    setActionError('');
    try {
      const fd = new FormData();
      fd.append('approved_amount', String(parseFloat(approvedAmount)));
      if (approveRemarks.trim()) fd.append('remarks', approveRemarks.trim());
      await approvePFAdvanceRequest(approveModal.id, fd);
      setSuccess('PF Advance request approved successfully.');
      setApproveModal(null);
      loadEmployeePFData();
    } catch (err) {
      setActionError(err.message || 'Failed to approve request.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleSubmitPaymentConfirmation = async (e) => {
    e.preventDefault();
    setActionSubmitting(true);
    setActionError('');
    try {
      const fd = new FormData();
      fd.append('payment_mode', confirmPaymentMode);
      if (confirmPaymentDoc) fd.append('payment_doc', confirmPaymentDoc);
      await confirmPFAdvancePayment(confirmPaymentModal.id, fd);
      setSuccess('PF Advance payment confirmed successfully.');
      setConfirmPaymentModal(null);
      loadEmployeePFData();
    } catch (err) {
      setActionError(err.message || 'Failed to confirm payment.');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Submit Rejection
  const handleSubmitReject = async (e) => {
    e.preventDefault();
    setActionSubmitting(true);
    setActionError('');
    try {
      await rejectPFAdvanceRequest(rejectModal.id, {
        rejection_reason: rejectionReason.trim()
      });
      setSuccess('PF Advance request rejected.');
      setRejectModal(null);
      loadEmployeePFData();
    } catch (err) {
      setActionError(err.message || 'Failed to reject request.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleOpenCreateModal = () => {
    const initType = 'REFUNDABLE';
    setRequestType(initType);
    const maxAmount = account?.eligible_refundable_amount || 0;
    setRequestedAmount(maxAmount > 0 ? String(maxAmount) : '');
    setReason('');
    setRemarks('');
    setCreateError('');
    setIsCreateModalOpen(true);
  };

  const handleTypeChange = (newType) => {
    setRequestType(newType);
    const maxAmount = newType === 'REFUNDABLE'
      ? (account?.eligible_refundable_amount || 0)
      : (account?.eligible_non_refundable_amount || 0);
    setRequestedAmount(maxAmount > 0 ? String(maxAmount) : '');
    setCreateError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    setCreateSubmitting(true);
    setCreateError('');

    const maxEligible = requestType === 'REFUNDABLE'
      ? (account?.eligible_refundable_amount || 0)
      : (account?.eligible_non_refundable_amount || 0);

    const parsed = parseFloat(requestedAmount);
    if (isNaN(parsed) || parsed <= 0) {
      setCreateError('Please enter a valid requested amount greater than zero.');
      setCreateSubmitting(false);
      return;
    }

    if (parsed > maxEligible) {
      const capPercent = requestType === 'REFUNDABLE' ? '50%' : '75%';
      setCreateError(`Requested amount cannot exceed maximum eligible limit of ${formatMoney(maxEligible)} (${capPercent} cap).`);
      setCreateSubmitting(false);
      return;
    }

    if (requestType === 'REFUNDABLE' && !account?.can_apply_refundable) {
      setCreateError(account?.refundable_apply_restriction || 'Refundable advance is not permitted at this time.');
      setCreateSubmitting(false);
      return;
    }

    try {
      const payload = {
        request_type: requestType,
        requested_amount: parsed,
        reason: reason.trim(),
        remarks: remarks.trim() || undefined
      };
      await submitEmployeePFAdvanceRequest(employeeId, payload);
      setSuccess('PF Advance request created successfully.');
      setIsCreateModalOpen(false);
      loadEmployeePFData();
    } catch (err) {
      setCreateError(err.message || 'Failed to create PF advance request.');
    } finally {
      setCreateSubmitting(false);
    }
  };


  const handleOpenVpfModal = () => {
    setVpfContributionType('FIXED_AMOUNT');
    setVpfFixedAmount('');
    setVpfPercentage('');
    setVpfEffectiveFrom(new Date().toISOString().slice(0, 10));
    setVpfEffectiveTo('');
    setVpfError('');
    setIsVpfModalOpen(true);
  };

  const handleSubmitVpfCreate = async (e) => {
    e.preventDefault();
    setVpfSubmitting(true);
    setVpfError('');
    try {
      const payload = {
        is_enabled: true,
        contribution_type: vpfContributionType,
        fixed_amount: vpfContributionType === 'FIXED_AMOUNT' ? parseFloat(vpfFixedAmount) : null,
        percentage_of_basic: vpfContributionType === 'PERCENTAGE_OF_BASIC' ? parseFloat(vpfPercentage) : null,
        effective_from_date: vpfEffectiveFrom,
        effective_to_date: vpfEffectiveTo || null,
      };
      await createEmployeeVpfRequest(employeeId, payload);
      setSuccess('VPF request created successfully.');
      setIsVpfModalOpen(false);
      loadEmployeePFData();
    } catch (err) {
      setVpfError(err.message || 'Failed to create VPF request.');
    } finally {
      setVpfSubmitting(false);
    }
  };

  const handleApproveVpf = async (vpfReqId) => {
    if (!window.confirm('Are you sure you want to approve this VPF request?')) return;
    setActionSubmitting(true);
    setError('');
    try {
      await approveVpfRequest(vpfReqId);
      setSuccess('VPF request approved successfully.');
      loadEmployeePFData();
    } catch (err) {
      setError(err.message || 'Failed to approve VPF request.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleSubmitRejectVpf = async (e) => {
    e.preventDefault();
    setActionSubmitting(true);
    setActionError('');
    try {
      await rejectVpfRequest(rejectVpfModal.id, vpfRejectionReason.trim());
      setSuccess('VPF request rejected.');
      setRejectVpfModal(null);
      loadEmployeePFData();
    } catch (err) {
      setActionError(err.message || 'Failed to reject VPF request.');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleCloseVpf = async (vpfReqId) => {
    if (!window.confirm('Are you sure you want to close this VPF request? This will stop VPF deductions.')) return;
    setActionSubmitting(true);
    setError('');
    try {
      await closeVpfRequest(vpfReqId);
      setSuccess('VPF request closed.');
      loadEmployeePFData();
    } catch (err) {
      setError(err.message || 'Failed to close VPF request.');
    } finally {
      setActionSubmitting(false);
    }
  };


  const formatMoney = (val) =>
    `₹${Math.round(Number(val || 0)).toLocaleString('en-IN')}`;

  const createMaxEligible = requestType === 'REFUNDABLE'
    ? (account?.eligible_refundable_amount || 0)
    : (account?.eligible_non_refundable_amount || 0);

  const createParsedAmount = parseFloat(requestedAmount);
  const isCreateAmountValid = !isNaN(createParsedAmount) && createParsedAmount > 0 && createParsedAmount <= createMaxEligible;
  const isCreateEligible = requestType === 'REFUNDABLE' ? (Boolean(account?.can_apply_refundable) && createMaxEligible > 0) : (createMaxEligible > 0);
  const canSubmitCreate = isCreateAmountValid && isCreateEligible && !createSubmitting;

  if (loading && !account) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        <Clock className="w-5 h-5 animate-spin mr-2 text-blue-600" />
        <span>Loading Employee PF details...</span>
      </div>
    );
  }

  if (!loading && !account) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm flex items-center space-x-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
        <div>
          <p className="font-bold">Unable to load PF Account information.</p>
          <p className="text-xs text-red-600 mt-0.5">{error || 'Failed to communicate with PF service.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub Navigation Bar */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        {[
          { id: 'overview', label: 'Overview', icon: Wallet },
          { id: 'advance', label: 'PF Advance & Approvals', icon: Shield },
          { id: 'ledger', label: 'PF Ledger', icon: FileText },
          { id: 'vpf', label: 'VPF', icon: TrendingUp }

        ].map((tab) => {
          const Icon = tab.icon;
          const active = subTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id)}
              className={`flex items-center space-x-2 px-3.5 py-1.5 rounded-lg text-xs font-bold transition ${
                active ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* OVERVIEW SUBTAB */}
      {subTab === 'overview' && account && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
              <p className="text-[11px] font-bold text-blue-600 uppercase">Current PF Balance</p>
              <p className="text-xl font-extrabold text-blue-900 mt-1">{formatMoney(account.current_pf_balance)}</p>
              {account.current_pf_balance_as_on && (
                <p className="text-[10px] text-blue-600 mt-1">Balance as on: {formatDate(account.current_pf_balance_as_on)}</p>
              )}
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
              <p className="text-[11px] font-bold text-gray-500 uppercase">PFS Contribution</p>
              <p className="text-xl font-extrabold text-gray-900 mt-1">{formatMoney(account.employee_contribution)}</p>
              {account.pfs_contribution_as_on && (
                <p className="text-[10px] text-gray-500 mt-1">Contribution as on: {formatDate(account.pfs_contribution_as_on)}</p>
              )}
            </div>
            <div className="bg-purple-50 border border-purple-100 p-4 rounded-xl">
              <p className="text-[11px] font-bold text-purple-600 uppercase">VPF Balance</p>
              <p className="text-xl font-extrabold text-purple-900 mt-1">{formatMoney(account.vpf_balance)}</p>
              {account.vpf_balance_as_on && (
                <p className="text-[10px] text-purple-600 mt-1">Balance as on: {formatDate(account.vpf_balance_as_on)}</p>
              )}
            </div>
            <div className="bg-green-50 border border-green-100 p-4 rounded-xl">
              <p className="text-[11px] font-bold text-green-600 uppercase">PF Advance Recovered</p>
              <p className="text-xl font-extrabold text-green-900 mt-1">{formatMoney(account.total_pf_advance_recovered || 0)}</p>
              {account.pf_advance_recovered_as_on && (
                <p className="text-[10px] text-green-600 mt-1">Recovered as on: {formatDate(account.pf_advance_recovered_as_on)}</p>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl">
              <p className="text-[11px] font-bold text-amber-600 uppercase">Outstanding Refundable</p>
              <p className="text-xl font-extrabold text-amber-800 mt-1">{formatMoney(account.outstanding_refundable_advance)}</p>
              {account.outstanding_refundable_as_on && (
                <p className="text-[10px] text-amber-600 mt-1">Outstanding as on: {formatDate(account.outstanding_refundable_as_on)}</p>
              )}
            </div>
          </div>

          {/* Historical Opening PF Initialization Card */}
          <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-2">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Historical Opening PF Initialization</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-gray-500 font-medium">Opening PF Balance</p>
                <p className="font-bold text-slate-900">{formatMoney(account.opening_pf_balance)}</p>
              </div>
              <div>
                <p className="text-gray-500 font-medium">Opening VPF Balance</p>
                <p className="font-bold text-slate-900">{formatMoney(account.opening_vpf_balance)}</p>
              </div>
              <div>
                <p className="text-gray-500 font-medium">Opening Balance As On</p>
                <p className="font-bold text-slate-900">{formatDate(account.opening_pf_balance_as_on, 'Not specified')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 p-4 rounded-xl space-y-1">
              <span className="text-xs font-bold text-indigo-700 uppercase">Refundable Max Eligibility (50%)</span>
              <p className="text-2xl font-black text-indigo-900">{formatMoney(account.eligible_refundable_amount)}</p>
              {!account.can_apply_refundable && (
                <p className="text-xs text-amber-700 mt-1">{account.refundable_apply_restriction}</p>
              )}
            </div>
            <div className="bg-white border border-gray-200 p-4 rounded-xl space-y-1">
              <span className="text-xs font-bold text-emerald-700 uppercase">Non-Refundable Max Eligibility (75%)</span>
              <p className="text-2xl font-black text-emerald-900">{formatMoney(account.eligible_non_refundable_amount)}</p>
            </div>
          </div>
        </div>
      )}

      {/* PF ADVANCE SUBTAB (APPROVE / REJECT) */}
      {subTab === 'advance' && (
        <div className="space-y-6">
          {/* Active Recovery Progress Display */}
          {recoveryInfo && recoveryInfo.has_active_recovery && recoveryInfo.recovery && (
            <div className="bg-slate-900 text-white rounded-xl p-5 shadow border border-slate-800 space-y-3">
              <div className="flex justify-between items-center">
                <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-300 text-xs font-bold rounded-full">
                  Active Refundable Recovery Schedule
                </span>
                <span className="text-xs text-gray-400">Installments: {recoveryInfo.recovery.installments_paid} / {recoveryInfo.recovery.total_installments}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div>
                  <p className="text-gray-400">Approved</p>
                  <p className="font-bold text-white">{formatMoney(recoveryInfo.recovery.approved_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Monthly Installment</p>
                  <p className="font-bold text-amber-300">{formatMoney(recoveryInfo.recovery.monthly_installment)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Recovered</p>
                  <p className="font-bold text-emerald-400">{formatMoney(recoveryInfo.recovery.recovered_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Outstanding</p>
                  <p className="font-bold text-amber-400">{formatMoney(recoveryInfo.recovery.outstanding_amount)}</p>
                </div>
              </div>
              <div className="w-full bg-white/10 h-2.5 rounded-full overflow-hidden">
                <div
                  className="bg-emerald-400 h-full rounded-full transition-all"
                  style={{ width: `${Math.min(100, (recoveryInfo.recovery.installments_paid / recoveryInfo.recovery.total_installments) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {recoveryInfo && !recoveryInfo.has_active_recovery && (
            <div className="bg-gray-50 border border-gray-200 text-gray-500 rounded-xl p-4 text-xs font-medium">
              No active refundable PF advance recovery schedule.
            </div>
          )}

          {/* Advances Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <span className="font-bold text-xs text-gray-700 uppercase">Advance Requests & HR Approval Actions</span>
              <button
                id="btn-create-pf-request"
                onClick={handleOpenCreateModal}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded shadow-sm transition"
              >
                <PlusCircle className="w-3.5 h-3.5" />
                <span>Create PF Request</span>
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 text-gray-600 uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-2.5">Request Created Date</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Requested</th>
                    <th className="px-4 py-2.5">Eligible</th>
                    <th className="px-4 py-2.5">Approved</th>
                    <th className="px-4 py-2.5">Decision Date</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Reason</th>
                    <th className="px-4 py-2.5 text-right">HR Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {advances.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center text-gray-400">No advance requests found.</td>
                    </tr>
                  ) : (
                    advances.map((adv) => (
                      <tr key={adv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{formatDate(adv.created_at, '-')}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 font-bold rounded-full ${
                            adv.request_type === 'REFUNDABLE' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {adv.request_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-gray-900">{formatMoney(adv.requested_amount)}</td>
                        <td className="px-4 py-3 text-gray-600">{formatMoney(adv.eligible_amount)}</td>
                        <td className="px-4 py-3 font-bold text-blue-700">{adv.approved_amount ? formatMoney(adv.approved_amount) : '-'}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDate(adv.approved_at, '—')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 font-bold rounded-full ${
                            adv.status === 'RECOVERING' || adv.status === 'IN_RECOVERY' ? 'bg-blue-100 text-blue-800' :
                            adv.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                            adv.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' :
                            adv.status === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {adv.status === 'RECOVERING' || adv.status === 'IN_RECOVERY' ? 'RECOVERING' : adv.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{adv.reason || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          {adv.status === 'PENDING' ? (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleOpenApprove(adv)}
                                className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white font-bold rounded shadow-sm flex items-center space-x-1"
                              >
                                <ThumbsUp className="w-3 h-3" />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => { setRejectModal(adv); setRejectionReason(''); setActionError(''); }}
                                className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white font-bold rounded shadow-sm flex items-center space-x-1"
                              >
                                <ThumbsDown className="w-3 h-3" />
                                <span>Reject</span>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end space-x-2">
                              <span className="text-gray-400 text-[11px]">
                                {adv.status === 'RECOVERING' || adv.status === 'IN_RECOVERY' ? 'In Recovery' :
                                 adv.status === 'COMPLETED' ? `Completed${adv.payment_mode ? ` (${adv.payment_mode.replace('_', ' ')})` : ''}` :
                                 adv.status === 'APPROVED' ? `Paid${adv.payment_mode ? ` (${adv.payment_mode.replace('_', ' ')})` : ''}` :
                                 'Processed'}
                              </span>
                              {adv.payment_document && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await downloadAdvanceDocument(adv.id);
                                    } catch (err) {
                                      setError(err.message || 'Failed to open document.');
                                    }
                                  }}
                                  className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="View Document"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* LEDGER SUBTAB */}
      {subTab === 'ledger' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm space-y-3">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center text-xs">
            <span className="font-bold text-gray-700 uppercase">Employee PF Passbook Ledger</span>
            <span className="text-gray-500">Total Entries: {ledgerData.total}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-100 text-gray-600 uppercase font-semibold">
                <tr>
                  <th className="px-4 py-2.5">Record Creation Date</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Remarks</th>
                  <th className="px-4 py-2.5 text-right">Credit</th>
                  <th className="px-4 py-2.5 text-right">Debit</th>
                  <th className="px-4 py-2.5 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ledgerData.items.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center text-gray-400">No ledger transactions found.</td>
                  </tr>
                ) : (
                  ledgerData.items.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-600">{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 font-bold rounded-full ${
                          r.transaction_type === 'CONTRIBUTION' ? 'bg-blue-100 text-blue-800' :
                          r.transaction_type === 'VPF' ? 'bg-purple-100 text-purple-800' :
                          r.transaction_type === 'ADVANCE_RECOVERY' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {r.transaction_type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-xs truncate">{r.remarks || '-'}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-emerald-600">{r.credit > 0 ? `+${formatMoney(r.credit)}` : '-'}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-red-600">{r.debit > 0 ? `-${formatMoney(r.debit)}` : '-'}</td>
                      <td className="px-4 py-2.5 text-right font-black text-gray-900">{formatMoney(r.running_balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {ledgerData.total > ledgerData.page_size && (
            <div className="px-4 py-3 border-t border-gray-200 flex justify-between items-center text-xs">
              <button
                disabled={ledgerData.page <= 1}
                onClick={() => handlePageChange(ledgerData.page - 1)}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 font-bold rounded"
              >
                Previous
              </button>
              <span className="text-gray-500">Page {ledgerData.page} of {Math.ceil(ledgerData.total / ledgerData.page_size)}</span>
              <button
                disabled={ledgerData.page * ledgerData.page_size >= ledgerData.total}
                onClick={() => handlePageChange(ledgerData.page + 1)}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 font-bold rounded"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* VPF SUBTAB */}
      {subTab === 'vpf' && (
        <div className="space-y-6">
          {/* Header Card */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Voluntary Provident Fund (VPF)</h3>
              <p className="text-xs text-gray-500 mt-0.5">Centralized Admin panel for configuring employee VPF contribution rules.</p>
            </div>
            <button
              onClick={handleOpenVpfModal}
              className="flex items-center space-x-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md transition"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Create VPF Request</span>
            </button>
          </div>

          {/* Active VPF Status Card */}
          {vpfContext && vpfContext.current_request && (
            <div className="bg-slate-900 text-white rounded-xl p-5 shadow border border-slate-800 space-y-3">
              <div className="flex justify-between items-center">
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${
                  vpfContext.current_request.status === 'ACTIVE' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                }`}>
                  Current VPF State: {vpfContext.current_request.status}
                </span>
                <span className="text-xs text-gray-400">
                  Effective From: {formatDate(vpfContext.current_request.effective_from_date, '-')}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                <div>
                  <p className="text-gray-400">Contribution Type</p>
                  <p className="font-bold text-white">
                    {vpfContext.current_request.contribution_type === 'FIXED_AMOUNT' ? 'Fixed Amount' : 'Percentage of Basic'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Deduction Value</p>
                  <p className="font-bold text-blue-300">
                    {vpfContext.current_request.contribution_type === 'FIXED_AMOUNT'
                      ? formatMoney(vpfContext.current_request.fixed_amount)
                      : `${vpfContext.current_request.percentage_of_basic}%`}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Approval Status</p>
                  <p className="font-bold text-white">{vpfContext.current_request.approval_status}</p>
                </div>
                <div>
                  <p className="text-gray-400">Action</p>
                  {vpfContext.current_request.status === 'ACTIVE' && (
                    <button
                      onClick={() => handleCloseVpf(vpfContext.current_request.id)}
                      className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded text-[10px]"
                    >
                      Stop/Close VPF
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* VPF Request History Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 font-bold text-xs text-gray-700 uppercase">
              VPF Request History
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 text-gray-600 uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-2.5">Created Date</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Value</th>
                    <th className="px-4 py-2.5">From Date</th>
                    <th className="px-4 py-2.5">To Date</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vpfHistory.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-6 text-center text-gray-400">No VPF requests found.</td>
                    </tr>
                  ) : (
                    vpfHistory.map((v) => (
                      <tr key={v.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{formatDate(v.created_at, '-')}</td>
                        <td className="px-4 py-3">{v.contribution_type === 'FIXED_AMOUNT' ? 'Fixed Amount' : 'Percentage of Basic'}</td>
                        <td className="px-4 py-3 font-bold text-gray-900">
                          {v.contribution_type === 'FIXED_AMOUNT' ? formatMoney(v.fixed_amount) : `${v.percentage_of_basic}%`}
                        </td>
                        <td className="px-4 py-3">{formatDate(v.effective_from_date, '-')}</td>
                        <td className="px-4 py-3">{formatDate(v.effective_to_date, 'Ongoing')}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 font-bold rounded-full ${
                            v.approval_status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                            v.approval_status === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {v.approval_status === 'PENDING_APPROVAL' ? 'PENDING' : v.approval_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {v.approval_status === 'PENDING_APPROVAL' ? (
                            <div className="flex justify-end space-x-1.5">
                              <button
                                onClick={() => handleApproveVpf(v.id)}
                                className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white font-bold rounded shadow-sm flex items-center space-x-0.5 text-[10px]"
                              >
                                <ThumbsUp className="w-2.5 h-2.5" />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => { setRejectVpfModal(v); setVpfRejectionReason(''); }}
                                className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white font-bold rounded shadow-sm flex items-center space-x-0.5 text-[10px]"
                              >
                                <ThumbsDown className="w-2.5 h-2.5" />
                                <span>Reject</span>
                              </button>
                            </div>
                          ) : v.status === 'ACTIVE' ? (
                            <button
                              onClick={() => handleCloseVpf(v.id)}
                              className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded text-[10px]"
                            >
                              Stop/Close
                            </button>
                          ) : (
                            <span className="text-gray-400 text-[10px]">{v.status}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {/* APPROVE DIALOG */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 border border-gray-100 shadow-2xl">
            <div className="flex justify-between items-center border-b border-gray-100 pb-2">
              <h3 className="font-bold text-gray-900 text-base">Approve PF Advance</h3>
              <button onClick={() => setApproveModal(null)} className="text-gray-400 font-bold text-lg">×</button>
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{actionError}</span>
              </div>
            )}

            <div className="bg-gray-50 p-3 rounded-xl space-y-1 text-xs text-gray-700">
              <p><strong>Advance Type:</strong> {approveModal.request_type}</p>
              <p><strong>Requested Amount:</strong> {formatMoney(approveModal.requested_amount)}</p>
              <p><strong>Max Eligible Amount:</strong> {formatMoney(approveModal.eligible_amount)}</p>
              {approveModal.request_type === 'REFUNDABLE' && recoveryInfo?.has_active_recovery && recoveryInfo?.recovery && (
                <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 space-y-1">
                  <p className="font-bold text-[11px] uppercase text-amber-800">Existing Active Recovery Consolidation</p>
                  <p>Previous Outstanding Balance: <strong>{formatMoney(recoveryInfo.recovery.outstanding_amount)}</strong></p>
                  <p>Total Recovery (New + Previous): <strong>{formatMoney(Number(approvedAmount || approveModal.requested_amount || 0) + Number(recoveryInfo.recovery.outstanding_amount))}</strong></p>
                  <p>Consolidated Monthly Installment (10 mos): <strong>{formatMoney(Math.round((Number(approvedAmount || approveModal.requested_amount || 0) + Number(recoveryInfo.recovery.outstanding_amount)) / 10))}</strong></p>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmitApprove} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Approved Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  readOnly
                  value={approvedAmount}
                  onChange={(e) => setApprovedAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm bg-gray-100 text-gray-500 cursor-not-allowed outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Approval Remarks</label>
                <textarea
                  rows="2"
                  value={approveRemarks}
                  onChange={(e) => setApproveRemarks(e.target.value)}
                  placeholder="Optional remarks"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setApproveModal(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionSubmitting}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold text-xs rounded-xl shadow-sm"
                >
                  {actionSubmitting ? 'Approving...' : 'Confirm Approval'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REJECT DIALOG */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 border border-gray-100 shadow-2xl">
            <div className="flex justify-between items-center border-b border-gray-100 pb-2">
              <h3 className="font-bold text-red-700 text-base">Reject PF Advance</h3>
              <button onClick={() => setRejectModal(null)} className="text-gray-400 font-bold text-lg">×</button>
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{actionError}</span>
              </div>
            )}

            <form onSubmit={handleSubmitReject} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Rejection Reason</label>
                <textarea
                  required
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide mandatory reason for rejection"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setRejectModal(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionSubmitting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-sm"
                >
                  {actionSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      )}

      {/* CREATE PF REQUEST DIALOG */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-lg font-bold text-gray-900">Create PF Request</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold text-lg">×</button>
            </div>

            {createError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{createError}</span>
              </div>
            )}

            <form onSubmit={handleSubmitCreate} className="space-y-4">
              {/* Employee Confirmation */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                Creating request for Employee ID: <strong>{employeeId}</strong>. Please confirm before submitting.
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Advance Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    id="btn-adv-type-refundable"
                    type="button"
                    onClick={() => handleTypeChange('REFUNDABLE')}
                    className={`p-3 rounded-xl border text-xs font-bold text-left transition ${
                      requestType === 'REFUNDABLE' ? 'border-indigo-600 bg-indigo-50 text-indigo-900 ring-2 ring-indigo-200' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold">Refundable</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Max 50% Cap • 10 Monthly Installments</p>
                  </button>

                  <button
                    id="btn-adv-type-non-refundable"
                    type="button"
                    onClick={() => handleTypeChange('NON_REFUNDABLE')}
                    className={`p-3 rounded-xl border text-xs font-bold text-left transition ${
                      requestType === 'NON_REFUNDABLE' ? 'border-emerald-600 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-200' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold">Non-Refundable</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Max 75% Cap • No Payroll Recovery</p>
                  </button>
                </div>
              </div>

              {/* Max Cap Info Box */}
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs flex justify-between items-center">
                <div>
                  <span className="text-gray-600 font-bold">
                    Eligible PF Advance Limit ({requestType === 'REFUNDABLE' ? '50% Cap' : '75% Cap'}):
                  </span>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {requestType === 'REFUNDABLE'
                      ? 'Maximum calculated as 50% of current PF balance (rounded to ₹10)'
                      : 'Maximum calculated as 75% of current PF balance'}
                  </p>
                </div>
                <span className="font-extrabold text-blue-900 text-sm">
                  {formatMoney(createMaxEligible)}
                </span>
              </div>

              {/* Active Recovery Block Warning */}
              {requestType === 'REFUNDABLE' && !account?.can_apply_refundable && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs">
                  <strong>Restriction:</strong> {account?.refundable_apply_restriction}
                </div>
              )}

              {/* Previous Recovery Balance Consolidation Info */}
              {requestType === 'REFUNDABLE' && account?.has_active_recovery && account?.active_recovery_outstanding > 0 && account?.can_apply_refundable && (
                <div className="p-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-xl text-xs space-y-0.5">
                  <p className="font-bold">Active Advance Rollover Notice:</p>
                  <p>
                    Employee has an active advance with an outstanding balance of <strong>{formatMoney(account.active_recovery_outstanding)}</strong>.
                    Upon approval, this remaining amount will be consolidated into the new 10-installment recovery schedule.
                  </p>
                </div>
              )}

              {/* Editable Requested Amount Input */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-gray-700 uppercase">
                    Requested PF Advance Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  {createMaxEligible > 0 && (
                    <button
                      id="btn-set-to-max"
                      type="button"
                      onClick={() => setRequestedAmount(String(createMaxEligible))}
                      className="text-[11px] font-bold text-blue-600 hover:text-blue-800 transition"
                    >
                      Set to Max ({formatMoney(createMaxEligible)})
                    </button>
                  )}
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 font-bold text-sm">
                    ₹
                  </span>
                  <input
                    id="input-requested-pf-amount"
                    type="number"
                    step="0.01"
                    min="1"
                    max={createMaxEligible}
                    required
                    value={requestedAmount}
                    onChange={(e) => setRequestedAmount(e.target.value)}
                    placeholder={`Enter amount up to ${formatMoney(createMaxEligible)}`}
                    className={`w-full pl-8 pr-3 py-2 border rounded-xl text-sm font-semibold outline-none transition ${
                      !requestedAmount || isNaN(createParsedAmount) || createParsedAmount <= 0
                        ? 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                        : createParsedAmount > createMaxEligible
                        ? 'border-red-500 bg-red-50/50 text-red-900 focus:ring-2 focus:ring-red-400'
                        : 'border-emerald-500 bg-emerald-50/30 text-gray-900 focus:ring-2 focus:ring-emerald-400'
                    }`}
                  />
                </div>
                {requestedAmount && !isNaN(createParsedAmount) && createParsedAmount > createMaxEligible && (
                  <p className="text-xs text-red-600 font-medium mt-1 flex items-center">
                    <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    Requested amount exceeds maximum eligible limit of {formatMoney(createMaxEligible)} ({requestType === 'REFUNDABLE' ? '50%' : '75%'} cap).
                  </p>
                )}
                {requestedAmount && !isNaN(createParsedAmount) && createParsedAmount <= 0 && (
                  <p className="text-xs text-red-600 font-medium mt-1 flex items-center">
                    <AlertCircle className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    Requested amount must be greater than zero.
                  </p>
                )}
                {isCreateAmountValid && (
                  <p className="text-[11px] text-emerald-700 font-medium mt-1">
                    ✓ Valid request amount ({formatMoney(createParsedAmount)} within {formatMoney(createMaxEligible)} limit)
                  </p>
                )}
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Reason</label>
                <textarea
                  id="input-pf-advance-reason"
                  required
                  rows="2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Specify reason for advance (e.g. Medical emergency, marriage, education)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Remarks Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Remarks</label>
                <textarea
                  id="input-pf-advance-remarks"
                  rows="2"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional remarks"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
                <button
                  id="btn-cancel-pf-advance"
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  id="btn-submit-pf-advance"
                  type="submit"
                  disabled={!canSubmitCreate}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-sm transition"
                >
                  {createSubmitting ? 'Creating...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* CREATE VPF REQUEST DIALOG */}
      {isVpfModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl border border-gray-100">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-lg font-bold text-gray-900">Create VPF Request</h3>
              <button onClick={() => setIsVpfModalOpen(false)} className="text-gray-400 hover:text-gray-600 font-bold text-lg">×</button>
            </div>

            {vpfError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{vpfError}</span>
              </div>
            )}

            <form onSubmit={handleSubmitVpfCreate} className="space-y-4">
              {/* Employee Confirmation */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                Configuring VPF request for Employee ID: <strong>{employeeId}</strong>.
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Contribution Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setVpfContributionType('FIXED_AMOUNT')}
                    className={`p-3 rounded-xl border text-xs font-bold text-left transition ${
                      vpfContributionType === 'FIXED_AMOUNT' ? 'border-indigo-600 bg-indigo-50 text-indigo-900' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold">Fixed Amount</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Deduct a constant monthly amount</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setVpfContributionType('PERCENTAGE_OF_BASIC')}
                    className={`p-3 rounded-xl border text-xs font-bold text-left transition ${
                      vpfContributionType === 'PERCENTAGE_OF_BASIC' ? 'border-emerald-600 bg-emerald-50 text-emerald-900' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-bold">Percentage of Basic</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Deduct a percentage of basic salary</p>
                  </button>
                </div>
              </div>

              {/* Fixed Amount Input */}
              {vpfContributionType === 'FIXED_AMOUNT' ? (
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Fixed Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={vpfFixedAmount}
                    onChange={(e) => setVpfFixedAmount(e.target.value)}
                    placeholder="Enter amount (e.g. 2000)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Percentage of Basic (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={vpfPercentage}
                    onChange={(e) => setVpfPercentage(e.target.value)}
                    placeholder="Enter percentage (e.g. 12)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Effective From</label>
                  <input
                    type="date"
                    required
                    value={vpfEffectiveFrom}
                    onChange={(e) => setVpfEffectiveFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Effective To (Optional)</label>
                  <input
                    type="date"
                    value={vpfEffectiveTo}
                    onChange={(e) => setVpfEffectiveTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsVpfModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={vpfSubmitting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-xs font-bold rounded-xl shadow-sm transition"
                >
                  {vpfSubmitting ? 'Creating...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REJECT VPF REQUEST DIALOG */}
      {rejectVpfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 border border-gray-100 shadow-2xl">
            <div className="flex justify-between items-center border-b border-gray-100 pb-2">
              <h3 className="font-bold text-red-700 text-base">Reject VPF Request</h3>
              <button onClick={() => setRejectVpfModal(null)} className="text-gray-400 font-bold text-lg">×</button>
            </div>

            {actionError && (
              <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{actionError}</span>
              </div>
            )}

            <form onSubmit={handleSubmitRejectVpf} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Rejection Reason</label>
                <textarea
                  required
                  rows="3"
                  value={vpfRejectionReason}
                  onChange={(e) => setVpfRejectionReason(e.target.value)}
                  placeholder="Provide mandatory reason for rejection"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setRejectVpfModal(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionSubmitting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-sm"
                >
                  {actionSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee360PFView;

